// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect, Fragment } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { Link, Button, withStyles } from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { func, object, arrayOf, bool, number } from 'prop-types';
import { push } from 'connected-react-router';

import ListContainer from '../../list';
import { AlertDialog } from '../../../components';
import {
  getCustomFieldsList,
  getCustomFieldsListTotalPages,
  getIsLoading,
  getCustomFieldsListTotalElements,
  getIsDeleteSuccess,
  getIsError,
  getErrors,
} from '../../../reducers/customFields';
import { getIsApiOwner, getIsPortalAdmin, getUserDetails } from '../../../reducers/portalConfig';
import { hasPublisherRole } from '../../../utils';
import { getI18n } from '../../../utils/intl';
import { fetchCustomFields, deleteCustomFields } from '../../../actions/customFields';
import {
  STATE_ENABLED,
} from './utils';
import { getStatusColor, getStatusLabel } from "../../Api/List/utils";
import { ALERT_SUCCESS } from '../../../constants';
import styles from './styles';

const GRID_ROWS_PER_PAGE_OPTIONS = [24, 48, 72, 96];
const GRID_ROWS_PER_PAGE_DEFAULT_OPTION = 24;

export const CustomFieldsListPage = (props) => {
  const {
    customFields,
    classes,
    fetchCustomFields,
    isLoading,
    totalElements,
    isDeleteSuccess,
    deleteCustomFields,
    pageErrors,
    isError,
    userContext,
    isPortalAdmin,
    isApiOwner,
    totalPages,
  } = props;
  const intl = getI18n(useIntl());

  useEffect(() => {
    if (!hasPublisherRole(userContext)) { props.push('/404'); }
  }, [userContext]);

  const [rowsPerPage, setRowsPerPage] = useState(
    GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  );

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [errors, setErrors] = useState([]);

  const [page, setPage] = useState(0);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  useEffect(() => {
    setErrors(isError ? pageErrors : []);
  }, [isError, pageErrors]);

  useEffect(() => {
    if (isDeleteSuccess) {
      notifyMessages(
        ALERT_SUCCESS,
        intl.getI18nMessage('label.custom.field.deleted'),
      );
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
    }
  }, [isDeleteSuccess]);

  useEffect(() => {
    isDeleteSuccess && fetchList({ page : 0, setRowsPerPage : GRID_ROWS_PER_PAGE_DEFAULT_OPTION });
  }, [isDeleteSuccess]);

  useEffect(() => {
    fetchList();
  }, [page, rowsPerPage])

  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };
  const onChangePage = (newPage) => {
    setErrors([]);
    setPage(newPage);
  };
  const onChangeRowsPerPage = (newRowsPerPage) => {
    setErrors([]);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const onDelete = (item) => {
    setErrors([]);
    setShowConfirmDialog(true);
    setSelectedRow(item);
  }

  const deleteConfirmDialog = () => {
    setShowConfirmDialog(false);
    deleteCustomFields({ uuid : selectedRow.uuid });
  }

  const fetchList = () => {
    fetchCustomFields({
      page : page,
      size : rowsPerPage,
    });
  }

  const getCustomFieldsListColumns = () => [
    {
      id: 'name',
      secondaryId: 'uuid',
      label: intl.getI18nFormattedMessage('label.custom.field'),
      format: (value, secondaryValue) => {
        return(
          <Link underline="none" href={`/publish/settings/custom-fields/edit/${secondaryValue}`}>
            {value}
          </Link>
        )
      },
    },
    {
      id: 'entityType',
      label: intl.getI18nFormattedMessage('label.custom.fields.list.column.form'),
      value: (item) => {
        switch (item.entityType) {
          case 'API':
            return intl.getI18nFormattedMessage(
              'label.custom.fields.list.column.form.api',
            );
          case 'APPLICATION':
            return intl.getI18nFormattedMessage(
              'label.custom.fields.list.column.form.application',
            );
          default:
            return item.entityType;
        }
      },
    },
    {
      id: 'type',
      label: intl.getI18nFormattedMessage('label.custom.fields.list.column.type'),
      value: (item) => {
        switch (item.type) {
          case 'TEXT':
            return intl.getI18nFormattedMessage(
              'label.custom.fields.list.column.type.text',
            );
          case 'SINGLE_SELECT':
            return intl.getI18nFormattedMessage(
              'label.custom.fields.list.column.type.single.select',
            );
          default:
            return item.type;
        }
      },
    },
    {
      id: 'required',
      label: intl.getI18nFormattedMessage(
        'label.required.field',
      ),
      value: (item) => (item.required ? 'Yes' : 'No'),
    },
    {
      id: 'status',
      label: intl.getI18nFormattedMessage(
        'label.status',
      ),
      value: (item) => {
        const statusIconStyle = {
          fill: getStatusColor(item.status),
        };
        return (
          <span
            className={classes.itemState}
          >
            <FiberManualRecordIcon size="0.82em" style={statusIconStyle} className={classes.statusIcon} />
            {getStatusLabel(item.status)}
          </span>);
      },
    },
    {
      id: 'uuid',
      label: intl.getI18nFormattedMessage('label.actions'),
      value: (item) => {
          return(
            <div className={classes.buttonContainer}>
              <Button
                id={`${'status'-item.uuid}`}
                variant="text"
                disabled={item.status === STATE_ENABLED}
                onClick={() => onDelete(item)}
                className={classes.deleteText}
              >
                {intl.getI18nFormattedMessage('label.delete.button')}
              </Button>
            </div>
          )
      },
    },
  ];

  useEffect(() => {
    if(!isApiOwner && !isPortalAdmin) {
      props.push('/404')
    }
  }, [isPortalAdmin, isApiOwner])

  return (
    <Fragment>
      {showConfirmDialog && (
        <AlertDialog
          isOpen={showConfirmDialog}
          title={intl.getI18nMessage('warning.status')}
          description={intl.getI18nMessage('label.custom.field.delete.confirmation.text')}
          submitText={intl.getI18nMessage('label.delete.button')}
          cancelText={intl.getI18nMessage('label.cancel.button')}
          onClose={() => setShowConfirmDialog(false)}
          onSubmit={() => deleteConfirmDialog()}
          onCancel={() => setShowConfirmDialog(false)}
          dialogId="custom-fields-list-delete-dialog"
          submitButtonClass={classes.submitButton}
        />
      )}
      <ListContainer
        listPageId="custom-fields-list-page"
        isLoading={isLoading}
        notificationId="custom-fields-list-notifications"
        notificationStatus={notificationStatus}
        setNotificationStatus={setNotificationStatus}
        notificationMessage={notificationMessage}
        setNotificationMessage={setNotificationMessage}
        pageHeaderTitle={intl.getI18nMessage(
          'label.custom.fields.list.page.title',
        )}
        addButtonLabel={intl.getI18nMessage('label.custom.fields.add')}
        onAdd={() => window.location.href='/publish/settings/custom-fields/add'}
        columns={getCustomFieldsListColumns(intl, classes) || []}
        rows={customFields}
        noResultsMessage={intl.getI18nMessage('label.custom.fields.no.results')}
        page={page}
        rowsPerPageOptions={GRID_ROWS_PER_PAGE_OPTIONS}
        totalElements={totalElements}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        onChangePreviousPage={onChangePreviousPage}
        onChangeNextPage={onChangeNextPage}
        errors={errors}
      />
    </Fragment>
  );
};

CustomFieldsListPage.propTypes = {
  customFields: arrayOf(object),
  classes: object,
  fetchCustomFields: func,
  push: func,
  isLoading: bool,
  totalElements: number,
  user: object,
  isDeleteSuccess: bool,
  deleteCustomFields: func,
  pageErrors: arrayOf(object),
  isError: bool,
  userContext: object,
  isPortalAdmin: bool,
  isApiOwner: bool,
  totalPages: number,
};

const mapStateToProps = (state) => ({
  customFields: getCustomFieldsList(state),
  isLoading: getIsLoading(state),
  totalElements: getCustomFieldsListTotalElements(state),
  totalPages : getCustomFieldsListTotalPages(state),
  user: getUserDetails(state),
  isDeleteSuccess: getIsDeleteSuccess(state),
  isError: getIsError(state),
  pageErrors: getErrors(state),
  userContext: getUserDetails(state),
  isPortalAdmin: getIsPortalAdmin(state),
  isApiOwner: getIsApiOwner(state),
});

const mapDispatchToProps = {
  push,
  fetchCustomFields,
  deleteCustomFields,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(CustomFieldsListPage);
